/**
 * FI-4824 (r173): a client-owned route keeps THIS navigation's skeleton on screen until its island commits.
 *
 * `mountClientOwnedRoute` rebuilds the navigation root when the release probe answers (or at once, for a route that
 * mounts before it), and the page's island then waits for its chunk. Without a carry the member saw an EMPTY container
 * for that wait (the blank `/messages` of FI-4644). The skeleton's nodes move into one wrapper placed as a SIBLING just
 * before the page's mount container, never inside it (rule 32: `createRoot` discards a container's children at its
 * first commit). The wrapper goes when the container first holds an element or text (the island's first non-empty
 * commit, its own loading state and `RecoverableIslandFallback` included), on the next navigation, and on a failure.
 *
 * Eager and DOM-only on purpose: it must not wait on the lazy skeleton module. Design: enhanced-navigation design §8.3.
 */

// FI-5409: `restoreScrollPosition` (NavigationHistoryState.ts) reads this attribute from the DOM as a literal, so a
// Back restore never settles on the carry's height. Rename both together (NavigationHistoryState.skeletonCarryRestore.spec.ts).
export const NAVIGATION_SKELETON_CARRY_ATTRIBUTE = "data-navigation-skeleton-carry";

const NAVIGATION_SEQUENCE_DATASET_KEY = "discussionNavigationSequence";
const SKELETON_SELECTOR = '[data-playwright-id$="-page-navigation-skeleton"]';

let activeCarry: { wrapper: HTMLElement; observer: MutationObserver | null } | null = null;

/**
 * Detaches the root's skeleton into a carry wrapper, or returns null when the root does not hold THIS navigation's
 * skeleton (another sequence's, none at all, or a cold path whose skeleton never drew).
 */
export function takeNavigationSkeletonForCarry(root: HTMLElement, navigationSequence: number): HTMLElement | null {
    removeNavigationSkeletonCarry();
    if (root.dataset[NAVIGATION_SEQUENCE_DATASET_KEY] !== `${navigationSequence}` || !root.querySelector(SKELETON_SELECTOR)) {
        return null;
    }
    const wrapper = document.createElement("div");
    wrapper.setAttribute(NAVIGATION_SKELETON_CARRY_ATTRIBUTE, `${navigationSequence}`);
    wrapper.setAttribute("aria-hidden", "true");
    // The skeleton's own classes keep laying out against the root (flex-1 on `/messages`' one-viewport root).
    wrapper.style.display = "contents";
    wrapper.append(...Array.from<Node>(root.childNodes));
    return wrapper;
}

function hasCommittedContent(container: HTMLElement): boolean {
    return container.childElementCount > 0 || (container.textContent ?? "").trim().length > 0;
}

/** Puts the carry just before the page's mount container and releases it on the container's first non-empty commit. */
export function placeNavigationSkeletonCarry(wrapper: HTMLElement, pageContainer: HTMLElement): void {
    removeNavigationSkeletonCarry();
    if (!pageContainer.parentNode || hasCommittedContent(pageContainer)) {
        return;
    }
    pageContainer.before(wrapper);
    const observer = typeof MutationObserver === "function"
        ? new MutationObserver(() => {
            if (hasCommittedContent(pageContainer)) {
                removeNavigationSkeletonCarry();
            }
        })
        : null;
    observer?.observe(pageContainer, { childList: true, characterData: true, subtree: true });
    activeCarry = { wrapper, observer };
}

/** Removes the carried skeleton, if any: the island committed, a new navigation started, or the navigation failed. */
export function removeNavigationSkeletonCarry(): void {
    const carry = activeCarry;
    activeCarry = null;
    if (!carry) {
        return;
    }
    carry.observer?.disconnect();
    carry.wrapper.remove();
}
